import React from "react";
import { Calendar, MapPin } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";

export const EventCard = ({ event, onViewDetails }) => {
  const getCategoryColor = (category) => {
    const colors = {
      "Hip-Hop/RNB": "bg-pink-500 text-white",
      "Afrobeat": "bg-emerald-500 text-white",
      "Dancehall/Caraïbes": "bg-blue-500 text-white",
    };
    return colors[category] || "bg-muted text-foreground";
  };

  const isSoldOut = event.sold_out || event.available_tickets <= 0;

  const isEventPast = (() => {
    const months = {
      "janvier": 0, "février": 1, "mars": 2, "avril": 3, "mai": 4, "juin": 5,
      "juillet": 6, "août": 7, "septembre": 8, "octobre": 9, "novembre": 10, "décembre": 11
    };
    const match = (event.date || "").match(/(\d{1,2})\s+(\w+)\s+(\d{4})/i);
    if (match) {
      const month = months[match[2].toLowerCase()];
      if (month !== undefined) {
        const eventDate = new Date(parseInt(match[3]), month, parseInt(match[1]), 23, 59);
        return eventDate < new Date();
      }
    }
    return false;
  })();

  return (
    <div
      data-testid={`event-card-${event.id}`}
      className={`group cursor-pointer animate-fadeIn ${isSoldOut || isEventPast ? 'opacity-75' : ''}`}
      onClick={() => onViewDetails(event.id)}
    >
      {/* Image — propre, ratio 4:5, aucun texte dessus */}
      <div className="relative aspect-[4/5] overflow-hidden rounded-xl bg-black/40">
        <ImageWithFallback
          src={event.image}
          alt={event.title}
          className={`w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700 ${isSoldOut || isEventPast ? 'grayscale-[30%]' : ''}`}
        />

        {/* Badges sur l'image : catégorie + prix */}
        <div className="absolute top-3 left-3">
          <span className={`px-2.5 py-1 ${getCategoryColor(event.category)} text-xs font-bold rounded-full uppercase tracking-wide shadow-lg`}>
            {event.category}
          </span>
        </div>
        <div className="absolute top-3 right-3">
          <div className={`px-3 py-1.5 font-bold rounded-full text-sm shadow-lg ${isSoldOut ? 'bg-gray-500 text-gray-300' : 'bg-white/95 text-gray-900'}`}>
            {event.price > 0 ? `${event.price}€` : "Gratuit"}
          </div>
        </div>

        {/* TERMINÉ / COMPLET badges */}
        {isEventPast && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-gray-700/90 text-gray-200 px-6 py-3 rounded-lg font-bold text-xl uppercase tracking-widest transform -rotate-12 shadow-2xl border-2 border-gray-500">
              TERMINÉ
            </div>
          </div>
        )}
        {isSoldOut && !isEventPast && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-red-600/90 text-white px-6 py-3 rounded-lg font-bold text-xl uppercase tracking-widest transform -rotate-12 shadow-2xl border-2 border-red-400" data-testid="sold-out-badge">
              COMPLET
            </div>
          </div>
        )}
      </div>

      {/* Infos SOUS l'image */}
      <div className="pt-3 pb-2 px-1">
        <h3 className="text-lg font-bold text-foreground leading-tight line-clamp-2 group-hover:text-primary transition-colors">
          {event.title}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">{event.artist}</p>
        <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            <span>{event.date}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" />
            <span>{event.venue}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
